/**
 * IonIntroHandoverChips — Fix A (S4 fix-wave regression): the ion-intro
 * `intro-handover` step's activation chips, rendered in the FOOTER (directly
 * above the composer) instead of relying on ChatEmptyState's empty-state
 * slot.
 *
 * Why this exists: `ChatMessageList` only renders its `empty` slot — where
 * ChatEmptyState's own copy of `ION_INTRO_HANDOVER_PROMPTS` lives, see
 * IonSurface.jsx — when `chatMessages` is EMPTY. Since the S4 fix-wave,
 * intro-drive fires a REAL `/chat/stream` turn, so by the time the user
 * reaches intro-handover the conversation already holds Ion's own
 * prompt+reply — `chatMessages` is never empty at this point, so
 * ChatEmptyState (and its chips) never renders, and the activation step
 * showed nothing but a bare composer.
 *
 * Placement: IonSurface only mounts this INSIDE `.ion-panel__chat` (the same
 * container `data-tour="ion-intro-handover"` spans), directly above
 * `<ChatInput>` — so the interactive overlay cutout that makes the handover
 * step click-through already covers this strip too; nothing new to wire on
 * the tour side.
 *
 * No duplication: IonSurface mounts this ONLY when chatMessages is
 * NON-EMPTY — the exact complement of ChatEmptyState's own empty-only
 * render — so exactly one of the two chip sources is ever on screen.
 *
 * Reuses ChatEmptyState's exact chip markup/classes (`.ion-empty__prompts`/
 * `.ion-empty__prompt`) for visual consistency; only a layout modifier
 * (`--inline`, ion.css) turns the vertical stack into a horizontal wrapping
 * row that fits the footer's fixed height.
 *
 * Part D3: also renders the gallery's SECONDARY entry point ("See what else
 * Ion can do" — spec Part C, step 3 copy) below the chips, which stay the
 * PRIMARY affordance of this step. Same streaming guard as the chips: while
 * the hero turn (or anything else) is still live, opening the gallery is
 * disabled too, for the same "don't let the user act into a live stream"
 * reason — not because the gallery itself would collide with it.
 */

import { chatStreaming } from '../../state/chatStore.js'
import { openCapabilityGallery } from '../../state/capabilityGalleryState.js'
import { sendChatMessage } from '../../hooks/useChatStream.js'

/**
 * @param {object} props
 * @param {string[]} props.prompts
 * @param {object} [props.contextHint] — passed through to sendChatMessage
 */
export function IonIntroHandoverChips({ prompts, contextHint = null }) {
  const streaming = chatStreaming.value

  function handleClick(prompt) {
    // Fix B (S4 fix-wave): a stream may already be live (the intro-drive
    // turn itself, allowed to run to completion across the handover — see
    // tourDefinitions.js) — never start a second one on top of it. Mirrors
    // ChatInput's `canSend` gate (chatStore.js), which is the same
    // chatStreaming check.
    if (streaming) return
    sendChatMessage(prompt, { contextHint })
  }

  function handleGalleryClick() {
    if (streaming) return
    openCapabilityGallery()
  }

  return (
    <div class="ion-handover-chips">
      <ul class="ion-empty__prompts ion-empty__prompts--inline">
        {prompts.map((prompt) => (
          <li key={prompt}>
            <button
              type="button"
              class="ion-empty__prompt"
              disabled={streaming}
              onClick={() => handleClick(prompt)}
            >
              {prompt}
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        class="ion-handover-chips__gallery-cta"
        disabled={streaming}
        onClick={handleGalleryClick}
      >
        See what else Ion can do
      </button>
    </div>
  )
}
